import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { api } from "@/lib/http.ts";
import type { SetUserNameReq } from "@/types/passwordlessAuth.ts";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

/**
 * Who we are talking to, asked once a session exists for someone nobody has
 * named yet: a phone sign-up, or a guest whose card carries the number a roster
 * offered in place of a name. A door that already holds a name worth keeping
 * offers it as `initialName`, so confirming it is one tap.
 *
 * The session already exists by the time this renders, so the answer is posted
 * as a signed-in person. A failure keeps them here rather than landing them
 * anonymous: they are signed in either way, and the retry costs one tap.
 */
export const NameStep = ({
  statsigName,
  initialName,
  onSaved,
}: {
  /** The door this step belongs to, for the button's Statsig event. */
  statsigName: string;
  /** A name the account already carries, offered for confirmation or correction. */
  initialName?: string;
  /** The name is on the account; carry on to wherever the door lands. */
  onSaved: () => void;
}) => {
  const [name, setName] = useState(initialName ?? "");
  const ready = name.trim().length > 0;

  const save = useMutation({
    mutationFn: async (req: SetUserNameReq) => {
      await api.post("/api/a/users/me/name", req);
    },
    onSuccess: onSaved,
  });

  return (
    <form
      className="flex w-full flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !save.isPending) save.mutate({ name: name.trim() });
      }}
    >
      <div className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-semibold">What should we call you?</h1>
        <p className="text-sm text-muted-foreground">This is the name Okthx will use for you.</p>
      </div>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ada Lovelace"
        autoComplete="name"
        aria-label="Your name"
        autoFocus
      />
      {save.isError && (
        <p className="text-sm text-destructive">Something went wrong. Please try again.</p>
      )}
      <Button
        type="submit"
        disabled={!ready || save.isPending}
        className="w-full"
        data-statsig-name={statsigName}
      >
        {save.isPending ? "Saving..." : "Continue"}
      </Button>
    </form>
  );
};
